import { GearSix, type Icon } from "@phosphor-icons/react";
import clsx from "clsx";
import { Fragment, type JSX, useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { typedObjectEntries } from "common/helpers/utils";

type ActionsType<Item extends object> = Record<
  string,
  {
    icon: Icon;
    getLabel: (eligibleItemsCount: number) => string;
    getEligibleItems: (selectedItems: Item[]) => Item[];
    isDanger?: true;
  }
>;

interface BulkManageMobileDrawerProps<
  Item extends object,
  Actions extends ActionsType<Item>,
> {
  drawerLabel: string;
  actions: Actions;
  selection: Item[];
  renderActionComponent: ({
    action,
    onClose,
    onBack,
  }: {
    action: keyof Actions;
    onClose: () => void;
    onBack: () => void;
  }) => JSX.Element;
}

export default function BulkManageMobileDrawer<
  Item extends object,
  Actions extends ActionsType<Item>,
>({
  drawerLabel,
  actions,
  selection,
  renderActionComponent,
}: BulkManageMobileDrawerProps<Item, Actions>) {
  const [action, setAction] = useState<keyof Actions>();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Drawer
      open={isOpen}
      onOpenChange={setIsOpen}
      onAfterClose={() => setAction(undefined)}
    >
      <DrawerTrigger asChild>
        <Button
          variant="secondary-purple"
          leftIcon={<GearSix weight="fill" />}
          size="md"
          className="only:flex-1 not-only:ml-auto"
        >
          Manage
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        {action ? (
          renderActionComponent({
            action,
            onBack: () => setAction(undefined),
            onClose: () => setIsOpen(false),
          })
        ) : (
          <>
            <DrawerHeader title={`Manage ${drawerLabel}`} />
            <DrawerBody>
              {typedObjectEntries(actions)
                .filter(
                  ([, { getEligibleItems }]) =>
                    getEligibleItems(selection).length,
                )
                .map(
                  ([
                    actionKey,
                    { icon: Icon, getLabel, getEligibleItems, isDanger },
                  ]) => (
                    <Fragment key={actionKey.toString()}>
                      <button
                        type="button"
                        className={clsx(
                          "px-2 py-2.5 flex items-center text-button-sm gap-x-1.5",
                          isDanger && "text-red-500",
                        )}
                        onClick={() => {
                          setAction(actionKey);
                        }}
                      >
                        <Icon weight="fill" size={20} />
                        {getLabel(getEligibleItems(selection).length)}
                      </button>
                      <div className="h-px bg-black-200 my-1 last:hidden" />
                    </Fragment>
                  ),
                )}
            </DrawerBody>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
